import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const HIGHLIGHT_DURATION_MS = 1800

export const HIGHLIGHT_PARAM = 'highlight'

export const highlightTargetId = (id: string) => `highlight-target-${id}`

interface UseHighlightTargetOptions {
  items: Array<{ id: string }> | undefined
  isReady?: boolean
}

export const useHighlightTarget = ({ items, isReady = true }: UseHighlightTargetOptions) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeId, setActiveId] = useState<string | null>(null)
  const processedIdRef = useRef<string | null>(null)

  useEffect(() => {
    const requestedId = searchParams.get(HIGHLIGHT_PARAM)
    if (!requestedId || !isReady || !items?.length) return
    if (processedIdRef.current === requestedId) return
    if (!items.some((item) => item.id === requestedId)) return

    const frame = requestAnimationFrame(() => {
      // mark as processed only once the work actually runs — if this frame is
      // cancelled by an effect re-run (e.g. items/searchParams changing while
      // data settles), the next run must stay free to retry, otherwise the
      // highlight is silently lost
      processedIdRef.current = requestedId
      const el = document.getElementById(highlightTargetId(requestedId))
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setActiveId(requestedId)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(HIGHLIGHT_PARAM)
          return next
        },
        { replace: true }
      )
    })

    return () => cancelAnimationFrame(frame)
  }, [searchParams, isReady, items, setSearchParams])

  useEffect(() => {
    if (!activeId) return
    const timer = window.setTimeout(() => setActiveId(null), HIGHLIGHT_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [activeId])

  return activeId
}
